import type { AnimationEvent } from '@angular/animations';
import { transition, trigger, useAnimation } from '@angular/animations';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule, ViewportRuler } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  inject,
  DOCUMENT,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  fadeIn,
  fadeOut,
  slideInHorizontal,
  slideInVertical,
  slideOutHorizontal,
  slideOutVertical,
} from '@freelancer/animations';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { Location } from '@freelancer/location';
import { Pwa, SwipeNavigationService } from '@freelancer/pwa';
import { TimeUtils } from '@freelancer/time-utils';
import { IconModule, IconSize } from '@freelancer/ui/icon';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import type { Subscription } from 'rxjs';
import { SubscriptionLike } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { OverlayZIndexToken } from '../overlay-z-index.service';
import { OverlayZIndexService } from '../overlay-z-index.service';
import {
  BottomDrawerSize,
  type DrawerBackdropBehavior,
  DrawerPosition,
  DrawerWidthSize,
  HandlerBottomMargin,
} from './drawer.types';
import { StickyModule } from '../sticky/sticky.module';
import { TESTING_ANIMATION_DISABLED } from '../ui.config';
import { ViewHeaderComponent } from '../view-header/view-header.component';

@Component({
  standalone: true,
  selector: 'fl-drawer',
  imports: [
    DragDropModule,
    IconModule,
    NgTemplateOutlet,
    ScrollingModule,
    StickyModule,
    ViewHeaderComponent,
  ],
  template: `
    <div
      #drawerContainerEl
      class="DrawerContainer"
      cdkScrollable
      [attr.data-position]="drawerPosition"
      [attr.data-dim-backdrop]="dimBackdropActive"
      [attr.data-interactable-body]="
        isBottomDrawer &&
        !dimBackdropActive &&
        backdropBehavior === 'none-interactable'
      "
    >
      @if (dimBackdropActive) {
        <div
          class="DrawerBackdrop"
          aria-label="Drawer backdrop"
          i18n-aria-label="Drawer backdrop"
          [attr.data-backdrop]="isOpen"
          [attr.data-full-width]="fullWidth"
          [attr.data-blur]="!!bottomSheetHeader"
          [attr.tabindex]="0"
          [@backdropAnimation]="state"
          (click)="handleBackdropClick()"
        ></div>
      }
      @if (!isBottomDrawer) {
        <div
          class="DrawerDialog"
          [attr.data-full-width]="fullWidth"
          [attr.data-size]="sizeDesktop"
          [@paneAnimationHorizontal]="{
            value: state,
            params: { distance: hideStatePosition },
          }"
          (@paneAnimationHorizontal.done)="animationDone($event)"
        >
          <ng-container *ngTemplateOutlet="drawerContent"></ng-container>
        </div>
      }
      @if (isBottomDrawer) {
        <div
          #drawerBottomSheet
          class="DrawerBottomSheet"
          [attr.data-dim-backdrop]="dimBackdropActive"
        >
          @if (bottomSheetHeader && isOpen) {
            <div
              class="DrawerBottomSheetHeader"
              [@paneAnimationVertical]="{
                value: state,
                params: { distance: hideStatePosition },
              }"
            >
              <ng-container
                *ngTemplateOutlet="bottomSheetHeader"
              ></ng-container>
            </div>
          }
          <div
            class="DrawerDialog desktop:hide"
            [attr.data-full-width]="fullWidth"
            [attr.data-bottom-drawer-size]="bottomDrawerSize"
            [attr.data-full-height-draggable]="isFullHeightDraggable"
            [@paneAnimationVertical]="{
              value: state,
              params: { distance: hideStatePosition },
            }"
            (@paneAnimationVertical.done)="animationDone($event)"
          >
            <ng-container *ngTemplateOutlet="drawerContent"></ng-container>
          </div>
        </div>
      }

      <ng-template #drawerContent>
        <div
          #drawerContentDrag
          class="DrawerContent"
          i18n-cdkDragLockAxis
          cdkDrag
          [cdkDragDisabled]="!(drawerPosition === DrawerPosition.BOTTOM)"
          cdkDragLockAxis="y"
          (cdkDragStarted)="onDragStarted($event)"
          (cdkDragMoved)="onDragMoved($event)"
          (cdkDragEnded)="onDragEnded($event)"
          [attr.data-edge-to-edge]="edgeToEdge"
          [attr.data-full-width]="fullWidth"
          [attr.data-dim-backdrop]="dimBackdropActive"
          [attr.data-full-height-draggable]="isFullHeightDraggable"
          (swiperight)="handleSwipeRight()"
          (swipeleft)="handleSwipeLeft()"
          (swipebottom)="handleSwipeBottom()"
        >
          @if (!hideCloseButton) {
            <fl-icon
              class="DrawerCloseButton"
              i18n-label="Close drawer button label"
              label="Close drawer"
              [name]="'ui-close'"
              [hoverAnimation]="'spin-and-fade'"
              [size]="IconSize.MID"
              [class.mobile:hide]="fullWidth"
              (click)="handleEscape()"
              [useIconFont]="true"
            ></fl-icon>
          }
          @if (fullWidth) {
            <div class="DrawerHeaderContainer mobile:show">
              <fl-view-header
                class="DrawerHeader"
                [flSticky]="true"
                [flStickyContext]="'drawer'"
                [flStickyOrder]="0"
                [flStickyBehaviour]="StickyBehaviour.ALWAYS"
                [flStickyPosition]="StickyPosition.TOP"
                [heading]="mobileHeaderTitle"
              ></fl-view-header>
            </div>
          }
          @if (isOpen) {
            <div
              class="DrawerContent-inner"
              [attr.data-edge-to-edge]="edgeToEdge"
              [attr.data-full-height-draggable]="isFullHeightDraggable"
            >
              @if (drawerPosition === DrawerPosition.BOTTOM) {
                @if (dragHandleOnly) {
                  <div
                    class="DrawerDragHandle"
                    cdkDragHandle
                  >
                    <div
                      class="Handler"
                      [attr.data-handler-bottom-margin]="handlerBottomMargin"
                    ></div>
                  </div>
                } @else {
                  <div
                    class="Handler"
                    [attr.data-handler-bottom-margin]="handlerBottomMargin"
                  ></div>
                }
              }
              <ng-template [ngTemplateOutlet]="drawerTemplate"></ng-template>
            </div>
          }
        </div>
      </ng-template>
    </div>
  `,
  styleUrls: ['./drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('paneAnimationHorizontal', [
      transition('* => visible', [
        useAnimation(slideInHorizontal, {
          params: { duration: '100', translateX: '{{ distance }}' },
        }),
      ]),
      transition('* => hidden', [
        useAnimation(slideOutHorizontal, {
          params: { duration: '100', translateX: '{{ distance }}' },
        }),
      ]),
    ]),
    trigger('paneAnimationVertical', [
      transition('* => visible', [
        useAnimation(slideInVertical, {
          params: { duration: '100', translateY: '{{ distance }}' },
        }),
      ]),
      transition('* => hidden', [
        useAnimation(slideOutVertical, {
          params: { duration: '100', translateY: '{{ distance }}' },
        }),
      ]),
    ]),
    trigger('backdropAnimation', [
      transition('visible => hidden', [
        useAnimation(fadeOut, { params: { duration: '100' } }),
      ]),
      transition('hidden => visible', [
        useAnimation(fadeIn, { params: { duration: '100' } }),
      ]),
    ]),
  ],
})
export class DrawerComponent implements OnDestroy, OnInit {
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private location = inject(Location);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private timeUtils = inject(TimeUtils);
  private swipeNavigation = inject(SwipeNavigationService);
  private pwa = inject(Pwa);
  private document = inject<Document>(DOCUMENT);
  private hostElementRef = inject(ElementRef);
  private viewportRuler = inject(ViewportRuler);
  private overlayZIndex = inject(OverlayZIndexService);
  readonly testingCloseAnimationDisabled? = inject(TESTING_ANIMATION_DISABLED, {
    optional: true,
  });

  @ViewChild('drawerBottomSheet') drawerBottomSheet: ElementRef<HTMLElement>;
  @ViewChild('drawerContentDrag') drawerContent: ElementRef;
  @ViewChild('drawerContainerEl') drawerContainerEl: ElementRef;

  private readonly DRAWER_HEIGHT_TRANSITION_MS = 100;

  drawerHeight: number;
  yDiff: number;
  IconSize = IconSize;
  DrawerPosition = DrawerPosition;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;

  private overlayToken?: OverlayZIndexToken;
  canRemoveOverflow = false;
  state = 'hidden';
  hideStatePosition = '10%';
  drawerPosition = DrawerPosition.RIGHT;
  isBottomDrawer = false;
  // False only for bottom drawer when `backdropBehavior` is not `dim`.
  dimBackdropActive = true;

  @Input() bottomDrawerSize = BottomDrawerSize.MID;
  @Input() closeOnBackdropClick = true;
  /**
   * Restricts drag-to-dismiss on bottom drawers to the grab handle only. Use
   * when the drawer holds its own scrollable content (e.g. a picker) whose
   * vertical scroll would otherwise be swallowed by the drag gesture — the
   * handle still drags the sheet, the body scrolls.
   */
  @Input() dragHandleOnly = false;

  /**
   * `none` and `none-interactable` only apply to bottom drawers.
   * Left and right drawers always have a dimmed backdrop.
   */
  @Input() backdropBehavior: DrawerBackdropBehavior = 'dim';
  @Input() edgeToEdge = false;
  @Input() fullHeightDraggable = false;
  @Input() handlerBottomMargin: HandlerBottomMargin = 'auto';
  @Input() hideCloseButton = false;
  @Input() mobileHeaderTitle: string;

  /** Defines the width of the drawer on desktop */
  @Input() sizeDesktop: DrawerWidthSize = 'fixed';

  @Input() set position(value: DrawerPosition) {
    this.drawerPosition = value;
    this.setHidePosition();
  }

  // Shows backdrop regardless of viewport width when set to false
  @Input() fullWidth = true;

  @Output() closeDrawer = new EventEmitter<void>();
  @Output() openDrawer = new EventEmitter<void>();

  @ContentChild('drawerTemplate')
  drawerTemplate: TemplateRef<any>;

  /**
   * Optional content rendered above a bottom drawer's dialog, on top of the
   * backdrop. Use to float context (e.g. the item being actioned) above the
   * action sheet. Only applies to bottom drawers.
   */
  @ContentChild('drawerBottomSheetHeader')
  bottomSheetHeader?: TemplateRef<any>;

  @HostBinding('class.IsActive') isOpen = false;

  private routeChangeSubscription?: Subscription;
  @RepetitiveSubscription()
  private drawerPopCloseSubscription?: SubscriptionLike;

  get isFullHeightDraggable(): boolean {
    return (
      this.fullHeightDraggable && this.drawerPosition === DrawerPosition.BOTTOM
    );
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.close();
  }

  ngOnInit(): void {
    this.routeChangeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this._close({ fromRouteChange: true });
      });

    this.isBottomDrawer = this.drawerPosition === DrawerPosition.BOTTOM;
    this.dimBackdropActive = !(
      this.isBottomDrawer && this.backdropBehavior !== 'dim'
    );
  }

  onDragStarted(event: any): void {
    this.drawerHeight = parseInt(
      window.getComputedStyle(this.drawerContent.nativeElement).height,
      10,
    );
    this.drawerContent.nativeElement.style.transition = 'none';
  }

  onDragMoved(event: any): void {
    event.source._dragRef.reset();
    this.yDiff = event.distance.y;

    // If full height draggable and bottom drawer, we allow dragging in both directions up to max viewport height
    if (this.isFullHeightDraggable) {
      const newHeight = this.drawerHeight - this.yDiff;
      const maxDraggableHeight = this.viewportRuler.getViewportSize().height;

      if (newHeight > maxDraggableHeight) {
        this.drawerContent.nativeElement.style.height = `${maxDraggableHeight}px`;
      } else if (newHeight > 0) {
        this.drawerContent.nativeElement.style.height = `${newHeight}px`;
      }
      return;
    }

    if (this.yDiff >= 0) {
      const newHeight = this.drawerHeight - this.yDiff;
      this.drawerContent.nativeElement.style.height = `${newHeight}px`;
    }
  }

  onDragEnded(event: any): void {
    this.drawerContent.nativeElement.style.transition = 'height 0.1s ease-out';

    // If full height draggable and bottom drawer, drag according to snap points, close drawer when 20vh
    if (this.isFullHeightDraggable) {
      const newHeight = this.drawerHeight - this.yDiff;
      const viewportHeight = this.viewportRuler.getViewportSize().height;
      const closeThreshold = viewportHeight * 0.2;

      if (closeThreshold > newHeight) {
        this.close();

        this.timeUtils.setTimeout(() => {
          this.drawerContent.nativeElement.style.height = `${this.drawerHeight}px`;
        }, this.DRAWER_HEIGHT_TRANSITION_MS);
        return;
      }

      const snapPoints = [
        viewportHeight * 0.25,
        viewportHeight * 0.5,
        viewportHeight * 0.75,
        viewportHeight,
      ];

      let closestSnap = snapPoints[0];
      let minDistance = Math.abs(newHeight - closestSnap);

      for (const snapPoint of snapPoints) {
        const distance = Math.abs(newHeight - snapPoint);
        if (distance < minDistance) {
          minDistance = distance;
          closestSnap = snapPoint;
        }
      }

      this.drawerContent.nativeElement.style.height = `${closestSnap}px`;
      this.drawerHeight = closestSnap;
      return;
    }

    if (this.yDiff > 10) {
      this.close();

      this.timeUtils.setTimeout(() => {
        this.drawerContent.nativeElement.style.height = `${this.drawerHeight}px`;
      }, this.DRAWER_HEIGHT_TRANSITION_MS);
      this.renderer.removeStyle(this.document.body, 'overflow');
    } else {
      this.drawerContent.nativeElement.style.height = `${this.drawerHeight}px`;
    }
  }

  close({ fromRouteChange = false }: { fromRouteChange?: boolean } = {}): void {
    this.renderer.removeStyle(this.document.body, 'overflow');
    this._close({ fromRouteChange });
  }

  private _close({
    fromRouteChange = false,
  }: { fromRouteChange?: boolean } = {}): void {
    if (this.isOpen) {
      this.state = 'hidden';
      this.setHidePosition();
      this.changeDetectorRef.markForCheck();

      if (this.testingCloseAnimationDisabled) {
        /**
         * Don't wait for `animationDone` callback which is being triggered later than expected in
         * consecutive UI tests. This effectively removes the close animation of the drawer.
         */
        this.isOpen = false;
        this.closeDrawer.emit();
        this.togglePageScrollState();
        this.moveContainerToHost();
      }

      if (this.drawerPopCloseSubscription) {
        this.drawerPopCloseSubscription.unsubscribe();
      }
      if (!fromRouteChange) {
        this.location.back();
      }
      setTimeout(() => {
        this.swipeNavigation.enableSwipeGestures(true);
      });
    }
  }

  setHidePosition(): void {
    this.hideStatePosition =
      this.drawerPosition === DrawerPosition.LEFT ? '-10%' : '10%';
  }

  handleBackdropClick(): void {
    if (this.closeOnBackdropClick && this.dimBackdropActive) {
      this.close();
    }
  }

  open(): void {
    if (!this.isOpen) {
      this.swipeNavigation.enableSwipeGestures(false);
      this.isOpen = true;
      this.moveContainerToBody();
      this.state = 'visible';
      this.openDrawer.emit();
      this.togglePageScrollState();
      this.drawerPopCloseSubscription = this.location._createBackButtonState({
        onPop: () => this._close({ fromRouteChange: true }),
      });
      this.changeDetectorRef.markForCheck();

      if (this.isFullHeightDraggable) {
        this.timeUtils.setTimeout(() => {
          if (this.drawerContent?.nativeElement) {
            const viewportHeight = this.viewportRuler.getViewportSize().height;
            this.drawerContent.nativeElement.style.height = `${viewportHeight}px`;
            this.drawerHeight = viewportHeight;
          }
        }, 0);
      }
    }
  }

  togglePageScrollState(): void {
    const target = this.document.body;
    const currentOverflow = target.style.overflow;

    // We only want to remove overflow when drawer component is the
    // one that set it. For instance, when drawer is placed within a modal
    // overflow is already set and we don't want to remove it
    // once drawer is closed
    if (this.isOpen && !currentOverflow) {
      this.canRemoveOverflow = true;
    }
    if (this.isOpen) {
      this.disableBodyScroll();
    } else if (!this.isOpen && this.canRemoveOverflow) {
      this.enableBodyScroll();
      this.canRemoveOverflow = false;
    }
  }

  disableBodyScroll(): void {
    if (
      !(this.isBottomDrawer && this.backdropBehavior === 'none-interactable')
    ) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    }
  }

  enableBodyScroll(): void {
    if (
      !(this.isBottomDrawer && this.backdropBehavior === 'none-interactable')
    ) {
      this.renderer.removeStyle(this.document.body, 'overflow');
      this.renderer.removeStyle(this.document.documentElement, 'overflow');
    }
  }

  animationDone(event: AnimationEvent): void {
    if (event.fromState === 'visible' && event.toState === 'hidden') {
      this.isOpen = false;
      this.closeDrawer.emit();
      this.togglePageScrollState();
      this.moveContainerToHost();

      // Reset dragging-related styles and state
      if (this.drawerContent?.nativeElement) {
        this.drawerContent.nativeElement.style.height = '';
        this.drawerContent.nativeElement.style.transition = '';
      }

      if (this.drawerBottomSheet?.nativeElement) {
        this.drawerBottomSheet.nativeElement.style.transform = '';
        this.drawerBottomSheet.nativeElement.style.bottom = '';
      }

      this.drawerHeight = 0;
    }
  }

  handleSwipeRight(): void {
    if (
      this.pwa.isInstalled() &&
      this.drawerPosition === DrawerPosition.RIGHT
    ) {
      this.close();
    }
  }

  handleSwipeLeft(): void {
    if (this.pwa.isInstalled() && this.drawerPosition === DrawerPosition.LEFT) {
      this.close();
    }
  }

  handleSwipeBottom(): void {
    if (
      this.pwa.isInstalled() &&
      this.drawerPosition === DrawerPosition.BOTTOM
    ) {
      this.close();
    }
  }

  /**
   * Moves the drawer container to document.body so it renders outside any
   * intermediate stacking context (e.g. modals, fixed navigation).
   */
  private moveContainerToBody(): void {
    if (this.drawerContainerEl?.nativeElement) {
      this.renderer.appendChild(
        this.document.body,
        this.drawerContainerEl.nativeElement,
      );
      this.overlayToken = this.overlayZIndex.acquire();
      this.drawerContainerEl.nativeElement.style.zIndex = String(
        this.overlayToken.zIndex,
      );
    }
  }

  private moveContainerToHost(): void {
    if (this.drawerContainerEl?.nativeElement) {
      this.renderer.appendChild(
        this.hostElementRef.nativeElement,
        this.drawerContainerEl.nativeElement,
      );
      this.drawerContainerEl.nativeElement.style.zIndex = '';
    }
    if (this.overlayToken) {
      this.overlayZIndex.release(this.overlayToken.token);
      this.overlayToken = undefined;
    }
  }

  ngOnDestroy(): void {
    this.enableBodyScroll();
    this.moveContainerToHost();

    if (this.routeChangeSubscription) {
      this.routeChangeSubscription.unsubscribe();
    }
  }
}
